<template>
    <div class="cs-builder" :class="{ 'cs-dark': isDark }">
        <!-- Header -->
        <div class="cs-header">
            <div class="cs-header-left">
                <button class="cs-btn cs-btn-ghost" @click="goBack" title="Back">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                </button>
                <div class="cs-logo">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                    </svg>
                    <span>CandleScript</span>
                </div>
            </div>
            <div class="cs-header-center">
                <input
                    class="cs-name-input"
                    v-model="indicator.name"
                    placeholder="Indicator Name"
                    @blur="autoSave"
                />
                <select class="cs-side-select" v-model="indicator.side" @change="autoSave">
                    <option value="onchart">Overlay (on chart)</option>
                    <option value="offchart">Separate Pane</option>
                </select>
            </div>
            <div class="cs-header-right">
                <button class="cs-btn cs-btn-ghost" @click="showTemplates = true" title="Templates">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                        <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
                    </svg>
                    <span class="cs-btn-label">Templates</span>
                </button>
                <button class="cs-btn cs-btn-ghost" @click="loadSaved" title="Load">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
                    </svg>
                    <span class="cs-btn-label">Load</span>
                </button>
                <button class="cs-btn cs-btn-ghost" @click="exportConfig" title="Export">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    <span class="cs-btn-label">Export</span>
                </button>
                <button class="cs-btn cs-btn-ghost" @click="$router.push({path:'/chart',query:{tab:'build'}})" title="Open strategies on the main chart">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 20V10M18 20V4M6 20v-4"/>
                    </svg>
                    <span class="cs-btn-label">Strategy</span>
                </button>
                <button class="cs-btn cs-btn-primary" @click="runIndicator" :disabled="isRunning">
                    <svg v-if="!isRunning" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polygon points="5 3 19 12 5 21 5 3"/>
                    </svg>
                    <span v-if="isRunning" class="cs-spinner-sm"></span>
                    <span>{{ isRunning ? 'Running...' : 'Run' }}</span>
                </button>
            </div>
        </div>

        <!-- Main Content -->
        <div class="cs-main">
            <!-- Left Panel: Editor -->
            <div class="cs-panel cs-editor-panel" :style="{ width: editorWidth + '%' }">
                <div class="cs-panel-header">
                    <span class="cs-panel-title">Editor</span>
                    <div class="cs-panel-actions">
                        <span v-if="lastRunTime" class="cs-run-time">{{ lastRunTime }}ms</span>
                        <span v-if="validationStatus" :class="'cs-status cs-status-' + validationStatus.type">
                            {{ validationStatus.message }}
                        </span>
                    </div>
                </div>
                <div class="cs-editor-wrapper" ref="editorWrapper">
                    <code-editor
                        ref="editor"
                        :code="indicator.code"
                        :errors="codeErrors"
                        :dark="isDark"
                        @change="onCodeChange"
                    />
                </div>
                <!-- Error Panel -->
                <div v-if="errors.length" class="cs-errors">
                    <div class="cs-errors-header">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/>
                            <line x1="9" y1="9" x2="15" y2="15"/>
                        </svg>
                        <span>{{ errors.length }} error{{ errors.length > 1 ? 's' : '' }}</span>
                    </div>
                    <div v-for="(err, i) in errors" :key="i" class="cs-error-item">
                        {{ err.message }}
                    </div>
                </div>
                <div v-if="warnings.length && !errors.length" class="cs-warnings">
                    <div v-for="(w, i) in warnings" :key="i" class="cs-warning-item">
                        ⚠ {{ w.message }}
                    </div>
                </div>
            </div>

            <!-- Resize Handle -->
            <div class="cs-resize-handle" @mousedown="startResize"></div>

            <!-- Right Panel: Config + Preview -->
            <div class="cs-panel cs-right-panel" :style="{ width: (100 - editorWidth) + '%' }">
                <!-- Tab Bar -->
                <div class="cs-tabs">
                    <button
                        v-for="tab in tabs"
                        :key="tab.id"
                        class="cs-tab"
                        :class="{ active: activeTab === tab.id }"
                        @click="activeTab = tab.id"
                    >
                        {{ tab.label }}
                    </button>
                </div>

                <!-- Config Tab -->
                <div v-show="activeTab === 'config'" class="cs-tab-content">
                    <input-config
                        :options="indicator.options"
                        :values="indicator.currentValues"
                        @update-options="onOptionsUpdate"
                        @update-values="onValuesUpdate"
                    />
                </div>

                <!-- Preview Tab -->
                <div v-show="activeTab === 'preview'" class="cs-tab-content cs-preview-tab">
                    <chart-preview
                        ref="preview"
                        :result="executionResult"
                        :config="indicator.plotConfig"
                        :side="indicator.side"
                        :dark="isDark"
                    />
                </div>

                <!-- Plot Config Tab -->
                <div v-show="activeTab === 'plot'" class="cs-tab-content">
                    <plot-config
                        :config="indicator.plotConfig"
                        @update="onPlotConfigUpdate"
                    />
                </div>
            </div>
        </div>

        <!-- Templates Modal -->
        <div v-if="showTemplates" class="cs-modal-overlay" @click.self="showTemplates = false">
            <template-picker
                @select="onTemplateSelect"
                @close="showTemplates = false"
            />
        </div>

        <!-- Load Modal -->
        <div v-if="showLoadModal" class="cs-modal-overlay" @click.self="showLoadModal = false">
            <saved-indicators
                @select="onLoadSelect"
                @delete="onDeleteSaved"
                @close="showLoadModal = false"
                @import="onImport"
            />
        </div>
    </div>
</template>

<script>
import CodeEditor from './CodeEditor.vue';
import InputConfig from './InputConfig.vue';
import ChartPreview from './ChartPreview.vue';
import PlotConfig from './PlotConfig.vue';
import TemplatePicker from './TemplatePicker.vue';
import SavedIndicators from './SavedIndicators.vue';
import {
    createIndicatorConfig,
    getDefaultValues,
} from '../../candle-script/schema.js';
import { getTemplate } from '../../candle-script/templates/index.js';
import {
    saveIndicator,
    exportIndicator,
    importIndicator,
} from '../../candle-script/storage.js';
import { CandleScriptExecutor } from '../../candle-script/engine/executor.js';
import { validateCode } from '../../candle-script/engine/static-analyzer.js';
import { generateSampleData } from './sample-data.js';

export default {
    name: 'IndicatorBuilder',
    components: {
        CodeEditor,
        InputConfig,
        ChartPreview,
        PlotConfig,
        TemplatePicker,
        SavedIndicators,
    },
    data() {
        const template = getTemplate('blank');
        const config = createIndicatorConfig({
            name: 'My Indicator',
            code: template.code,
            options: template.options,
            plotConfig: template.plotConfig,
            side: template.side,
        });
        config.currentValues = getDefaultValues(template.options);

        return {
            indicator: config,
            executor: null,
            isRunning: false,
            executionResult: null,
            errors: [],
            warnings: [],
            codeErrors: [],
            lastRunTime: null,
            validationStatus: null,
            activeTab: 'preview',
            tabs: [
                { id: 'preview', label: 'Preview' },
                { id: 'config', label: 'Inputs' },
                { id: 'plot', label: 'Plot Style' },
            ],
            editorWidth: 55,
            isResizing: false,
            showTemplates: false,
            showLoadModal: false,
            sampleData: null,
            autoSaveTimer: null,
        };
    },
    computed: {
        isDark() {
            return this.$store ? this.$store.getters.isDarkTheme : true;
        },
    },
    async created() {
        this.executor = new CandleScriptExecutor();
        this.sampleData = generateSampleData(500);

        // Check if loading from route params
        if (this.$route && this.$route.query && this.$route.query.id) {
            const { getIndicator } = require('../../candle-script/storage.js');
            const saved = getIndicator(this.$route.query.id);
            if (saved) {
                this.indicator = saved;
            }
        }
    },
    async mounted() {
        try {
            await this.executor.init();
        } catch (e) {
            console.warn('[CandleScript] Worker init failed, using fallback:', e);
        }
        // Auto-run on mount
        this.$nextTick(() => this.runIndicator());
    },
    beforeDestroy() {
        if (this.executor) {
            this.executor.destroy();
        }
        if (this.autoSaveTimer) {
            clearTimeout(this.autoSaveTimer);
        }
    },
    methods: {
        goBack() {
            if (this.$router) {
                this.$router.back();
            }
        },

        onCodeChange(code) {
            this.indicator.code = code;
            this.indicator.updatedAt = Date.now();

            // Live validation
            const validation = validateCode(code);
            this.codeErrors = validation.errors;
            if (validation.valid) {
                this.validationStatus = { type: 'success', message: '✓ Valid' };
            } else {
                this.validationStatus = { type: 'error', message: `${validation.errors.length} error(s)` };
            }

            // Debounced auto-run
            this.debouncedRun();
        },

        debouncedRun() {
            if (this._runTimer) clearTimeout(this._runTimer);
            this._runTimer = setTimeout(() => this.runIndicator(), 800);
        },

        async runIndicator() {
            if (this.isRunning) return;
            this.isRunning = true;
            this.errors = [];
            this.warnings = [];

            try {
                const result = await this.executor.execute(
                    this.indicator.code,
                    this.sampleData,
                    this.indicator.currentValues || getDefaultValues(this.indicator.options)
                );

                if (result.success) {
                    this.executionResult = result.result;
                    this.lastRunTime = result.elapsed;
                    this.errors = [];
                    this.warnings = result.warnings || [];
                    this.validationStatus = {
                        type: 'success',
                        message: `✓ ${result.cached ? 'Cached' : result.elapsed + 'ms'}`,
                    };
                } else {
                    this.errors = result.errors;
                    this.warnings = result.warnings || [];
                    this.executionResult = null;
                    this.validationStatus = {
                        type: 'error',
                        message: `${result.errors.length} error(s)`,
                    };
                }
            } catch (err) {
                this.errors = [{ type: 'runtime', message: err.message }];
                this.validationStatus = { type: 'error', message: 'Runtime error' };
            } finally {
                this.isRunning = false;
            }
        },

        onOptionsUpdate(options) {
            this.indicator.options = options;
            this.indicator.currentValues = getDefaultValues(options);
            this.autoSave();
        },

        onValuesUpdate(values) {
            this.indicator.currentValues = { ...values };
            this.debouncedRun();
        },

        onPlotConfigUpdate(config) {
            this.indicator.plotConfig = config;
            this.autoSave();
        },

        onTemplateSelect(template) {
            this.indicator = createIndicatorConfig({
                name: template.name,
                code: template.code,
                options: template.options,
                plotConfig: template.plotConfig,
                side: template.side,
                category: template.category,
            });
            this.indicator.currentValues = getDefaultValues(template.options);
            this.showTemplates = false;
            this.executionResult = null;
            this.$nextTick(() => this.runIndicator());
        },

        autoSave() {
            if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
            this.autoSaveTimer = setTimeout(() => {
                saveIndicator(this.indicator);
            }, 1000);
        },

        loadSaved() {
            this.showLoadModal = true;
        },

        onLoadSelect(config) {
            this.indicator = { ...config };
            if (!this.indicator.currentValues) {
                this.indicator.currentValues = getDefaultValues(this.indicator.options);
            }
            this.showLoadModal = false;
            this.executionResult = null;
            this.$nextTick(() => this.runIndicator());
        },

        onDeleteSaved(id) {
            const { deleteIndicator } = require('../../candle-script/storage.js');
            deleteIndicator(id);
        },

        exportConfig() {
            exportIndicator(this.indicator);
        },

        async onImport() {
            try {
                const config = await importIndicator();
                this.indicator = createIndicatorConfig(config);
                if (!this.indicator.currentValues) {
                    this.indicator.currentValues = getDefaultValues(this.indicator.options);
                }
                this.showLoadModal = false;
                this.$nextTick(() => this.runIndicator());
            } catch (e) {
                console.warn('[CandleScript] Import failed:', e);
            }
        },

        startResize(e) {
            this.isResizing = true;
            const startX = e.clientX;
            const startWidth = this.editorWidth;
            const container = this.$el.querySelector('.cs-main');
            const totalWidth = container.offsetWidth;

            const onMove = (ev) => {
                const dx = ev.clientX - startX;
                const pct = startWidth + (dx / totalWidth) * 100;
                this.editorWidth = Math.max(25, Math.min(75, pct));
            };

            const onUp = () => {
                this.isResizing = false;
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
            };

            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
    },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════
   CandleScript Builder - Styles
   ═══════════════════════════════════════════ */

.cs-builder {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: var(--tc-bg-primary, #fff);
    color: var(--tc-text-primary, #131722);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    overflow: hidden;
}

/* Header */
.cs-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    height: 52px;
    border-bottom: 1px solid var(--tc-border-primary, #e0e3eb);
    background: var(--tc-bg-secondary, #f8f9fd);
    flex-shrink: 0;
}

.cs-header-left,
.cs-header-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.cs-header-center {
    display: flex;
    align-items: center;
    gap: 12px;
}

.cs-logo {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 15px;
    color: var(--tc-accent, #2962ff);
}

.cs-name-input {
    background: var(--tc-input-bg, #fff);
    border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 14px;
    font-weight: 500;
    color: var(--tc-text-primary, #131722);
    width: 200px;
    outline: none;
    transition: border-color 0.2s;
}
.cs-name-input:focus {
    border-color: var(--tc-accent, #2962ff);
}

.cs-side-select {
    background: var(--tc-input-bg, #fff);
    border: 1px solid var(--tc-input-border, #e0e3eb);
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 13px;
    color: var(--tc-text-primary, #131722);
    outline: none;
    cursor: pointer;
}

/* Buttons */
.cs-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
}

.cs-btn-ghost {
    background: transparent;
    color: var(--tc-text-secondary, #787b86);
}
.cs-btn-ghost:hover {
    background: var(--tc-bg-hover, rgba(0,0,0,0.04));
    color: var(--tc-text-primary, #131722);
}

.cs-btn-primary {
    background: var(--tc-accent, #2962ff);
    color: #fff;
}
.cs-btn-primary:hover {
    background: var(--tc-accent-hover, #1e50d8);
}
.cs-btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.cs-btn-label {
    display: none;
}
@media (min-width: 900px) {
    .cs-btn-label { display: inline; }
}

/* Main Layout */
.cs-main {
    display: flex;
    flex: 1;
    overflow: hidden;
}

.cs-panel {
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.cs-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    border-bottom: 1px solid var(--tc-border-primary, #e0e3eb);
    background: var(--tc-bg-secondary, #f8f9fd);
    flex-shrink: 0;
    height: 36px;
}

.cs-panel-title {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--tc-text-secondary, #787b86);
}

.cs-panel-actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

.cs-run-time {
    font-size: 11px;
    color: var(--tc-text-tertiary, #9ca3af);
    font-family: 'SF Mono', 'Fira Code', monospace;
}

.cs-status {
    font-size: 11px;
    font-weight: 500;
    padding: 2px 8px;
    border-radius: 4px;
}
.cs-status-success {
    color: var(--tc-green, #22c55e);
    background: var(--tc-green-bg, rgba(34,197,94,0.1));
}
.cs-status-error {
    color: var(--tc-red, #ef4444);
    background: var(--tc-red-bg, rgba(239,68,68,0.1));
}

/* Editor Panel */
.cs-editor-panel {
    border-right: 1px solid var(--tc-border-primary, #e0e3eb);
}

.cs-editor-wrapper {
    flex: 1;
    overflow: hidden;
}

/* Resize Handle */
.cs-resize-handle {
    width: 4px;
    cursor: col-resize;
    background: transparent;
    transition: background 0.2s;
    flex-shrink: 0;
}
.cs-resize-handle:hover {
    background: var(--tc-accent, #2962ff);
}

/* Right Panel */
.cs-right-panel {
    display: flex;
    flex-direction: column;
}

/* Tabs */
.cs-tabs {
    display: flex;
    border-bottom: 1px solid var(--tc-border-primary, #e0e3eb);
    background: var(--tc-bg-secondary, #f8f9fd);
    flex-shrink: 0;
}

.cs-tab {
    padding: 10px 20px;
    font-size: 13px;
    font-weight: 500;
    color: var(--tc-text-secondary, #787b86);
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition: all 0.15s;
}
.cs-tab:hover {
    color: var(--tc-text-primary, #131722);
}
.cs-tab.active {
    color: var(--tc-accent, #2962ff);
    border-bottom-color: var(--tc-accent, #2962ff);
}

.cs-tab-content {
    flex: 1;
    overflow-y: auto;
    padding: 0;
}

.cs-preview-tab {
    padding: 0;
}

/* Errors */
.cs-errors {
    border-top: 1px solid var(--tc-red, #ef4444);
    background: var(--tc-red-bg, rgba(239,68,68,0.05));
    padding: 8px 16px;
    max-height: 120px;
    overflow-y: auto;
    flex-shrink: 0;
}

.cs-errors-header {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--tc-red, #ef4444);
    margin-bottom: 4px;
}

.cs-error-item {
    font-size: 12px;
    color: var(--tc-red, #ef4444);
    padding: 2px 0;
    font-family: 'SF Mono', 'Fira Code', monospace;
}

.cs-warnings {
    border-top: 1px solid #f59e0b;
    background: rgba(245, 158, 11, 0.05);
    padding: 8px 16px;
    max-height: 80px;
    overflow-y: auto;
    flex-shrink: 0;
}

.cs-warning-item {
    font-size: 12px;
    color: #f59e0b;
    padding: 2px 0;
}

/* Modal */
.cs-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

/* Spinner */
.cs-spinner-sm {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: cs-spin 0.6s linear infinite;
}

@keyframes cs-spin {
    to { transform: rotate(360deg); }
}
</style>
